import {
  identify as amplitudeIdentify,
  Identify,
  reset as amplitudeReset,
  setUserId,
  track as amplitudeTrack,
} from '@amplitude/analytics-browser';
import { objectKeysToSnakePascalCase } from '@shared/utils';

type AmplitudeSessionParameters = {
  broker_id?: string;
  email?: string;
  name?: string;
  brokerage_id?: string;
  brokerage_name?: string;
};

type AmplitudeEventParams = {
  client_id?: string | null;
  case_id?: string;
  banks?: string[];
  bank?: string;
  destination_type?: string;
  destinations?: string[];
  bank_products?: string[];
  error_message?: unknown;
  redirection_action?: string;
  total_fields?: number;
  pending_fields?: number;
  digital_form_generated?: number;
  proposal_id?: string | null;
  filters?: string;
  broker_id?: string;
  application_rank?: number;
  credit_price?: number;
  credit_quantity?: number;
  credit_bundle?: string;
  credit_bundle_quantity?: number;
  blocked?: 0 | 1;

  // Allows additional custom properties
  [key: string]: unknown;

  // Context related attributes
  useContext?: boolean;
  addToContext?: boolean;
};

export const identifyObj = new Identify();

const identifyAmplitudeSession = ({
  broker_id, email, name, brokerage_id, brokerage_name,
} : AmplitudeSessionParameters) => {
  if (!identifyObj.getUserProperties().$set) {
    setUserId(broker_id);
    amplitudeIdentify(identifyObj
      .set('broker_id', broker_id ?? '')
      .set('brokerage_id', brokerage_id ?? '')
      .set('brokerage_name', brokerage_name ?? '')
      .set('email', email ?? '')
      .set('name', name ?? '')
      .set('business_identifier', APPLICATION_BUSINESS_IDENTIFIER));
  }
};

let context: AmplitudeEventParams = {};

export const addToAmplitudeEventContext = (params: Partial<AmplitudeEventParams>) => {
  context = { ...context, ...params };
};

export const clearAmplitudeEventContext = () => {
  context = {};
};

const trackAmplitudeEvent = (eventName: string, eventParams?: AmplitudeEventParams) => {
  const { useContext, addToContext, ...restParams } = eventParams || {};

  if (addToContext) {
    addToAmplitudeEventContext(restParams);
  }

  // Parse error_message
  const modifiedParams = eventParams
    ? {
      ...restParams,
      error_message: (() => {
        try {
          return JSON.stringify(eventParams.error_message);
        } catch {
          return null;
        }
      })(),
      ...(useContext ? context : {}),
    }
    : {};

  // @ts-ignore
  if (!modifiedParams?.error_message) delete modifiedParams.error_message;

  const formattedParams = modifiedParams ? objectKeysToSnakePascalCase(modifiedParams) : undefined;
  amplitudeTrack(eventName, formattedParams);
};

const resetAmplitudeIdentity = () => amplitudeReset();

const setAdditionalUserParams = (params: Partial<AmplitudeSessionParameters>) => {
  const identify = new Identify();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      identify.set(key, value);
    }
  });

  amplitudeIdentify(identify);
};

export {
  identifyAmplitudeSession, trackAmplitudeEvent,
  resetAmplitudeIdentity, setAdditionalUserParams,
};
